import { useState, useEffect, useRef } from "react";
import { useTheme } from "../data/data";

/* ════════════════════════════════════════════════════════════════
   LANGUAGES — edit this array to add/remove languages.
   Codes must be valid Google Translate codes (e.g. "ar", "zh-CN").
   ════════════════════════════════════════════════════════════════ */
const LANGUAGES = [
  { code: "en",    name: "English",    flag: "🇬🇧", short: "EN" },
  { code: "hi",    name: "हिन्दी",        flag: "🇮🇳", short: "HI" },
  { code: "ar",    name: "العربية",     flag: "🇸🇦", short: "AR" },
  { code: "es",    name: "Español",    flag: "🇪🇸", short: "ES" },
  { code: "fr",    name: "Français",   flag: "🇫🇷", short: "FR" },
  { code: "zh-CN", name: "中文",          flag: "🇨🇳", short: "ZH" },
  { code: "sw",    name: "Kiswahili",  flag: "🇰🇪", short: "SW" },
];

/* Read the current language from Google Translate's cookie */
function readCurrentLang() {
  if (typeof document === "undefined") return "en";
  const m = document.cookie.match(/googtrans=\/[^/]+\/([^;]+)/);
  return m ? m[1] : "en";
}

/* Switch language by setting Google's cookie + reloading the page */
function setLanguage(code) {
  if (code === "en") {
    document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
  } else {
    document.cookie = `googtrans=/en/${code}; path=/;`;
  }
  window.location.reload();
}

export default function LanguageSwitcher() {
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("en");
  const wrapRef = useRef(null);

  // On mount: detect current language + inject Google Translate script
  useEffect(() => {
    setCurrent(readCurrentLang());

    if (window.__gtInjected) return;
    window.__gtInjected = true;

    window.googleTranslateElementInit = () => {
      // eslint-disable-next-line no-undef
      new window.google.translate.TranslateElement({
        pageLanguage: "en",
        includedLanguages: LANGUAGES.map(l => l.code).join(","),
        autoDisplay: false,
      }, "google_translate_element");
    };

    const s = document.createElement("script");
    s.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const currentLang = LANGUAGES.find(l => l.code === current) || LANGUAGES[0];

  return (
    <>
      {/* Hidden mount-point for Google Translate */}
      <div id="google_translate_element" style={{ display: "none" }} />

      {/* Hide Google's default UI globally — we use our own */}
      <style>{`
        .goog-te-banner-frame.skiptranslate,
        .goog-te-gadget,
        .goog-logo-link,
        .goog-te-balloon-frame,
        .skiptranslate > iframe { display: none !important; }
        body { top: 0 !important; position: static !important; }
        .goog-tooltip, .goog-tooltip:hover { display: none !important; }
        .goog-text-highlight { background: transparent !important; box-shadow: none !important; }
        @keyframes langDropdownIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div ref={wrapRef} style={{ position: "relative" }} className="notranslate">
        <button
          onClick={() => setOpen(o => !o)}
          aria-label="Change language"
          style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "transparent",
            border: `1.5px solid ${theme.border}`,
            borderRadius: 7,
            padding: "0.4rem 0.65rem",
            cursor: "pointer",
            fontSize: "0.74rem",
            color: theme.textSecondary,
            fontFamily: "'DM Sans',sans-serif",
            fontWeight: 600,
            letterSpacing: "0.04em",
            transition: "all 0.22s",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.color = theme.primary; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.color = theme.textSecondary; }}
        >
          <span style={{ fontSize: "0.95rem", lineHeight: 1 }}>{currentLang.flag}</span>
          <span>{currentLang.short}</span>
          <span style={{ fontSize: "0.55rem", marginLeft: 1, transform: open ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s", opacity: 0.7 }}>▼</span>
        </button>

        {open && (
          <div style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            background: theme.cardBg,
            border: `1px solid ${theme.border}`,
            borderRadius: 9,
            boxShadow: theme.shadowHover,
            minWidth: 180,
            zIndex: 1000,
            overflow: "hidden",
            animation: "langDropdownIn 0.15s ease-out",
          }}>
            <div style={{
              fontSize: "0.62rem",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: theme.textMuted,
              fontWeight: 700,
              padding: "0.65rem 0.9rem 0.4rem",
            }}>Choose Language</div>
            {LANGUAGES.map(lang => (
              <button
                key={lang.code}
                onClick={() => setLanguage(lang.code)}
                style={{
                  display: "flex", alignItems: "center", gap: 11,
                  width: "100%",
                  background: lang.code === current ? theme.sectionAlt : "transparent",
                  border: "none",
                  padding: "0.6rem 0.9rem",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  color: theme.textPrimary,
                  fontFamily: "'DM Sans',sans-serif",
                  fontWeight: lang.code === current ? 700 : 500,
                  textAlign: "left",
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = theme.sectionAlt}
                onMouseLeave={e => e.currentTarget.style.background = lang.code === current ? theme.sectionAlt : "transparent"}
              >
                <span style={{ fontSize: "1.15rem", lineHeight: 1 }}>{lang.flag}</span>
                <span style={{ flex: 1 }}>{lang.name}</span>
                {lang.code === current && <span style={{ color: theme.accent, fontSize: "0.9rem" }}>✓</span>}
              </button>
            ))}
            <div style={{
              fontSize: "0.65rem",
              color: theme.textMuted,
              padding: "0.55rem 0.9rem",
              borderTop: `1px solid ${theme.border}`,
              fontStyle: "italic",
              textAlign: "center",
            }}>
              Powered by Google Translate
            </div>
          </div>
        )}
      </div>
    </>
  );
}
